<template>
  <section class="section" v-if="currentUser?.has_view_access_to_commissions_dashboard">
    <div class="container">
      <div class="columns">
        <div class="column">

          <div class="columns is-desktop mb-8">

            <div class="column is-8">

              <div class="columns is-desktop">
                <div class="column is-6">
                  <div class="field">
                    <div class="control">
                      <input type="text" class="input" placeholder="Search by email or Order ID" v-model="searchText" @input="changeSearchText">
                    </div>
                  </div>
                </div>
                <div class="column is-6">
                  <date-picker v-model="dates" range placeholder="Period" style="width: 100%;"></date-picker>
                </div>
              </div>

              <div class="columns is-desktop">
                <div class="column is-3" v-if="currentUser?.has_full_view_access_to_commissions_dashboard">
                  Campaign Manager
                  <div class="field">
                    <div class="control">
                      <div class="select is-fullwidth">
                        <select v-model="campaignManager">
                          <option value="all">All</option>
                          <option v-for="cm in Object.values(WHITE_GLOVE_CAMPAIGN_MANAGERS)" :key="cm.key" :value="cm.key">{{ cm.first_name }}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="column is-3" v-if="currentUser?.has_full_view_access_to_commissions_dashboard">
                  Commission Owner
                  <div class="field">
                    <div class="control">
                      <div class="select is-fullwidth">
                        <select v-model="commissionOwner">
                          <option value="all">All</option>
                          <option v-for="cm in Object.values(WHITE_GLOVE_CAMPAIGN_MANAGERS)" :key="cm.key" :value="cm.key">{{ cm.first_name }}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="column is-3">
                  Sales Tag
                  <div class="field">
                    <div class="control">
                      <div class="select is-fullwidth">
                        <select v-model="salesTag">
                          <option value="all">All</option>
                          <option v-for="cm in Object.values(AVAILABLE_SALES_TAGS)" :key="cm.key" :value="cm.key">{{ cm.label }}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="column is-3">
                  Order Type
                  <div class="field">
                    <div class="control">
                      <div class="select is-fullwidth">
                        <select v-model="orderType">
                          <option value="all">All</option>
                          <option value="audit_order">Audit Order</option>
                          <option value="silo_order">Silo Order</option>
                          <option value="quick_content_order">Quick Content Order</option>
                          <option value="balance_top_up_order">Balance Top Up</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="columns is-desktop">
                <div class="column is-4">
                  <button class="button is-info is-fullwidth" @click="onFilter">Search</button>
                </div>
              </div>

            </div>

            <div class="column is-4">
              <div class="white-common-box p-4" style="overflow: initial;">
                <div class="mb-2" style="font-size: 1rem;">
                  Passive orders: <b>{{ aggregations?.count_passive || 0 }}</b>
                </div>
                <div class="mb-2" style="font-size: 1rem;">
                  Active orders: <b>{{ aggregations?.count_active || 0 }}</b>
                </div>
                <div class="mb-3" style="font-size: 1rem;">
                  Total commissions amount: <b>{{ formatCurrency(aggregations?.commissions_total || 0) }}</b>
                </div>
                <div>
                  <button @click="exportAll" class="button is-link is-fullwidth mb-3" style="font-weight: 700;">Export Orders</button>
                  <button v-if="currentUser?.has_full_view_access_to_commissions_dashboard" @click="exportAggregations" class="button is-link is-fullwidth mb-3" style="background-color: var(--v-greenColor-base); font-weight: 700;">Export Analytics (for active filters)</button>
                  <button v-if="currentUser?.has_full_view_access_to_commissions_dashboard" class="button is-link is-fullwidth mb-3" @click="exportResults(null, 'All time')" style="background-color: var(--v-greenColor-base); font-weight: 700;">Export Analytics (All time results)</button>
                  <b-dropdown
                    v-if="currentUser?.has_full_view_access_to_commissions_dashboard"
                    aria-role="list"
                    class="export-dropdown"
                  >
                    <template #trigger>
                      <button class="button is-link is-fullwidth" style="background-color: var(--v-greenColor-base); font-weight: 700;">Export Analytics (monthly results)</button>
                    </template>
                    <b-dropdown-item v-for="option in getMonthlyResultsDateOptions()" @click="exportResults(option.date, option.key)" :key="option.key">{{ option.label }}</b-dropdown-item>
                  </b-dropdown>
                </div>
              </div>
              <div v-if="currentUser?.has_edit_access_to_commissions_dashboard" class="mt-2 d-flex justify-end">
                <a href="#" @click.stop="showPercentagesModal = true" style="font-size: 1rem; font-weight: bold;">Edit Campaign Manager Percentages</a>
              </div>
            </div>

          </div>

          <b-table
            :data="records"
            :loading="isLoading"
            paginated
            per-page="100"
          >
            <template slot-scope="props">
              <b-table-column field="userId" label="User ID" sortable>{{ props.row.userId }}</b-table-column>
              <b-table-column field="userEmail" label="Email" sortable>{{ props.row.userEmail }}</b-table-column>
              <b-table-column field="order_type" label="Order Type" sortable>{{ props.row.order_type }}</b-table-column>
              <b-table-column field="id" label="Order ID" sortable numeric>{{ props.row.id }}</b-table-column>
              <b-table-column field="order_date_timestamp" label="Order Date" sortable numeric>{{ props.row.order_date }}</b-table-column>
              <b-table-column field="purchase_amount" label="Amount ($)" sortable numeric>
                <span>
                  {{formatCurrency(props.row.purchase_amount)}}
                </span>
              </b-table-column>
              <b-table-column field="campaign_manager" label="Campaign Manager" sortable numeric>{{ props.row.campaign_manager }}</b-table-column>
              <b-table-column field="commission_owner" label="Commission Owner" sortable numeric>{{ props.row.commission_owner }}</b-table-column>
              <b-table-column field="payment_method" label="Payment Method" sortable numeric>{{props.row.payment_method}}</b-table-column>
              <b-table-column field="sales_tag" label="Sales tag" sortable numeric>
                <b-dropdown
                  v-if="currentUser?.has_edit_access_to_commissions_dashboard"
                  aria-role="list" @click.native.stop
                  class="sales-tag-dropdown"
                >
                  <template #trigger>
                    <v-btn
                      style="min-width: 110px;"
                      class="sales-tag-btn text-none"
                      x-small
                    >
                      <template v-if="props.row.sales_tag === 'passive'">Passive sale</template>
                      <template v-else-if="props.row.sales_tag === 'active'"><b>Active sale</b></template>
                      <template v-else-if="!props.row.sales_tag">-</template>
                      <template v-else>{{ props.row.sales_tag }}</template>
                    </v-btn>
                  </template>
                  <b-dropdown-item :disabled="props.row.sales_tag === 'passive'" aria-role="listitem" @click.native.stop="updateSale(props.row, {sales_tag: 'passive', update_commission: true})">Set as passive sale</b-dropdown-item>
                  <b-dropdown-item :disabled="props.row.sales_tag === 'active'" aria-role="listitem" @click.native.stop="updateSale(props.row, {sales_tag: 'active', update_commission: true})" style="width: initial; font-size: inherit; display: block;">Set as <b>active sale</b></b-dropdown-item>
                </b-dropdown>
                <template v-else >
                  {{ props.row.sales_tag }}
                </template>
              </b-table-column>
              <b-table-column field="commission_percentage" label="Commission Percentage" sortable numeric>
                <div v-if="props.row.payment_method !== 'balance' && currentUser?.has_edit_access_to_commissions_dashboard && isEditingPercentageForId === props.row.id" class="d-flex align-center gap10">
                  <v-text-field
                    outlined
                    dense
                    hide-details
                    sm
                    color="greenColor"
                    type="number"
                    style="width: 50px;"
                    v-model="customPercentageFieldValue"
                  >
                  </v-text-field>
                  <b-tooltip
                    label="Save"
                    type="is-dark"
                  >
                    <i class="material-icons" style="cursor: pointer; color: var(--v-greenColor-base);" @click="updateSale(props.row, {'custom_commission_percentage': Number(customPercentageFieldValue), update_commission: true})">check</i>
                  </b-tooltip>
                  <b-tooltip
                    label="Remove Custom Percentage"
                    type="is-dark"
                  >
                    <i class="material-icons" style="cursor: pointer; color: var(--v-redColor-base);" @click="updateSale(props.row, {'custom_commission_percentage': null, update_commission: true})">delete</i>
                  </b-tooltip>
                  <b-tooltip
                    label="Cancel"
                    type="is-dark"
                  >
                    <i class="material-icons" @click="isEditingPercentageForId = null" style="cursor: pointer;">clear</i>
                  </b-tooltip>
                </div>
                <template v-else>
                  {{ props.row.commission_percentage }}% {{ props.row.has_custom_commission_percentage ? '(Custom)': '' }} <i v-if="currentUser?.has_edit_access_to_commissions_dashboard && props.row.payment_method !== 'balance'" class="material-icons" @click="isEditingPercentageForId = props.row.id; customPercentageFieldValue = props.row.commission_percentage;" style="cursor: pointer;">edit</i>
                </template>
              </b-table-column>
              <b-table-column field="commission_amount" label="Commission Amount" sortable numeric>{{formatCurrency(props.row.commission_amount)}}</b-table-column>
            </template>
          </b-table>
        </div>
      </div>

      <div v-if="showPercentagesModal" class="modal" :class="{'is-active': true}">
        <div class="modal-background" @click.stop="showPercentagesModal = false"></div>
        <div class="modal-content">
          <div class="box modal-container">
            <div class="modal-content-root">
              <p class="title-font is-size-4 mainColor--text">Edit Commission Owner Percentages</p>
              <div class="mainColor--text message-text" style="font-size: 1rem; line-height: 150%;">

                <div
                  v-for="campaign_manager in Object.values(WHITE_GLOVE_CAMPAIGN_MANAGERS)" :key="campaign_manager.key"
                  class="mb-4"
                >
                  <div style="font-size: 1rem; font-weight: bold;">
                    {{ campaign_manager.first_name }}
                  </div>

                  <div v-if="percentagesData?.default">
                    <table class="table">
                      <thead>
                        <tr>
                          <th width="200"></th>
                          <th width="200" v-for="[sales_tag, sales_tag_data] in Object.entries(AVAILABLE_SALES_TAGS)" :key="sales_tag">{{ sales_tag_data['label'] }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="[order_type, order_type_data] in Object.entries(percentagesData.default)" :key="order_type">
                          <td>
                            {{ order_type }}
                          </td>
                          <td v-for="sales_tag in Object.keys(AVAILABLE_SALES_TAGS)" :key="sales_tag">
                            <div v-if="isEditingPercentageForCM?.key === campaign_manager.key && isEditingPercentageForCM?.sales_tag === sales_tag && isEditingPercentageForCM?.order_type === order_type" class="d-flex align-center gap10">
                              <v-text-field
                                outlined
                                dense
                                hide-details
                                sm
                                color="greenColor"
                                type="number"
                                style="width: 50px;"
                                v-model="customCMPercentageFieldValue"
                              >
                              </v-text-field>
                              <b-tooltip
                                label="Save"
                                type="is-dark"
                              >
                                <i class="material-icons" style="cursor: pointer; color: var(--v-greenColor-base);" @click="updateCMPercentage(campaign_manager.key, {order_type, sales_tag, 'custom_commission_percentage': Number(customCMPercentageFieldValue)})">check</i>
                              </b-tooltip>
                              <b-tooltip
                                label="Remove Custom Percentage"
                                type="is-dark"
                              >
                                <i class="material-icons" style="cursor: pointer; color: var(--v-redColor-base);" @click="updateCMPercentage(campaign_manager.key, {order_type, sales_tag, 'custom_commission_percentage': null})">delete</i>
                              </b-tooltip>
                              <b-tooltip
                                label="Cancel"
                                type="is-dark"
                              >
                                <i class="material-icons" @click="isEditingPercentageForCM = null" style="cursor: pointer;">clear</i>
                              </b-tooltip>
                            </div>
                            <template v-else>
                              {{ getPercentageForCM(campaign_manager.key, order_type, sales_tag).final }}% {{ getPercentageForCM(campaign_manager.key, order_type, sales_tag).custom !== undefined ? '(Custom)': '' }} <i class="material-icons" @click="isEditingPercentageForCM = {'key': campaign_manager.key, order_type, sales_tag}; customPercentageFieldValue = getPercentageForCM(campaign_manager.key, order_type, sales_tag).final;" style="cursor: pointer;">edit</i>
                            </template>


                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            </div>
          </div>
          <button
            class="custom-modal-close-btn"
            aria-label="close"
            @click="() => (showPercentagesModal = false)">
            <i class="material-icons">close</i>
          </button>
        </div>
      </div>

    </div>
  </section>
</template>

<script>
import { mixin } from '@/components/mixin'

import { mixin as exportmixin } from '@/components/export_mixin'
import { toLocalDateString, formatCurrency } from '@/utils'
import {
  AVAILABLE_SALES_TAGS,
  WHITE_GLOVE_CAMPAIGN_MANAGERS,
} from "@/components/plans_constants"
import moment from 'moment'
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
  components: { DatePicker },
  mixins: [mixin, exportmixin],
  data() {
    return {
      dates: [moment().subtract(1, 'months').toDate(), moment().toDate()],
      records: [],
      aggregations: {},
      isLoading: false,
      searchText: '',
      salesTag: 'all',
      campaignManager: 'all',
      commissionOwner: 'all',
      orderType: 'all',
      isEditingPercentageForId: null,
      customPercentageFieldValue: 0,
      WHITE_GLOVE_CAMPAIGN_MANAGERS: WHITE_GLOVE_CAMPAIGN_MANAGERS,
      AVAILABLE_SALES_TAGS: AVAILABLE_SALES_TAGS,

      showPercentagesModal: false,
      percentagesData: {},
      isEditingPercentageForCM: null,
      customCMPercentageFieldValue: 0,
    }
  },
  computed: {
    currentUser() {
      return this.$store.state.user
    }
  },
  methods: {
    formatCurrency: formatCurrency,
    exportRecords(records) {
      const rows = records.map((record) =>
        [
          record.userId,
          record.userEmail,
          record.order_type,
          record.id,
          record.order_date ? moment.utc(record.order_date).format() : '',
          record.purchase_amount,
          record.campaign_manager,
          record.commission_owner,
          record.sales_tag,
          record.commission_percentage,
          record.commission_amount,

        ].join(',')
      );
      
      const data = ['User ID,Email,Order Type,Order ID,Order Date,Amount ($),Campaign Manager,Commission Owner,Sales tag,Commission Percentage,Commission Amount']
        .concat(rows)
        .join('\n');
      const now = new Date();
      const fileName = "Commissions_" + now.getUTCFullYear() + "_" + (now.getUTCMonth() + 1) + "_" + now.getUTCDate() + ".csv"
      this.exportToCSV(data, fileName)
    },
    exportAll() {
      this.exportRecords(this.records)
    },
    exportAggregations() {
      const rows = [
        [
          this.aggregations.count_active,
          this.aggregations.count_passive,
          this.aggregations.commissions_total_active,
          this.aggregations.purchase_amount_total_active,
          this.aggregations.commissions_total_passive,
          this.aggregations.purchase_amount_total_passive,
          this.aggregations.commissions_total,
          this.aggregations.purchase_amount_total,
          this.aggregations.count_clients,
          this.aggregations.count_clients_older_than_1_month,
          this.aggregations.purchase_amount_total_avg_per_client,
        ].join(',')
      ]

      const data = ['Active Orders Count,Passive Orders Count,Active Commissions ($),Active Revenue ($),Passive Commissions ($),Passive Revenue ($),Total Commissions ($),Total Revenue ($),Clients Count,Clients Older Than 1 Month Count,Revenue/Client Avg ($)']
        .concat(rows)
        .join('\n');
      const now = new Date();
      const fileName = "Commissions_Aggregations_" + now.getUTCFullYear() + "_" + (now.getUTCMonth() + 1) + "_" + now.getUTCDate() + ".csv"
      this.exportToCSV(data, fileName)
    },
    getMonthlyResultsDateOptions(){
      const options = []

      for (let index = 0; index <= 11; index++) {
        const d = new Date();
        d.setMonth(d.getMonth() - index, 1);
        options.push({
          'date': d,
          'key': moment(d).format('YYYY-MM'),
          'label': moment(d).format('MMMM YYYY'),
        })
      }

      return options
    },
    exportResults(monthDate, period) {
      this.$store.commit('showLoading')

      const rows = [
        ['Period,Campaign Manager,Active Orders Count,Passive Orders Count,Active Commissions ($),Active Revenue ($),Passive Commissions ($),Passive Revenue ($),Total Commissions ($),Total Revenue ($),Clients Count,Clients Older Than 1 Month Count,Revenue/Client Avg ($)']
      ]

      Promise.all(
        Object.values(WHITE_GLOVE_CAMPAIGN_MANAGERS).map((campaign_manager) => {

          let params = [
            `commissionOwner=${campaign_manager.key}`,
          ]

          if (monthDate) {
            let startDate = moment(monthDate).startOf('month').toDate();
            let endDate = moment(monthDate).endOf('month').toDate();
            if (startDate) {
              params.push(`startDate=${startDate.getTime()}`)
            }
            if (endDate) {
              params.push(`endDate=${endDate.getTime()}`)
            }
          }

          params = params.join('&')

          return this.$store.dispatch('getOrdersCommissionsForAdmin', { params })
            .then(({ data }) => {
              rows.push([
                period,
                campaign_manager.first_name,
                data.aggregations.count_active,
                data.aggregations.count_passive,
                data.aggregations.commissions_total_active,
                data.aggregations.purchase_amount_total_active,
                data.aggregations.commissions_total_passive,
                data.aggregations.purchase_amount_total_passive,
                data.aggregations.commissions_total,
                data.aggregations.purchase_amount_total,
                data.aggregations.count_clients,
                data.aggregations.count_clients_older_than_1_month,
                data.aggregations.purchase_amount_total_avg_per_client,
              ].join(','))
            })            
        })
      )
        .then(() => {
          const fileName = "Results_" + period + ".csv"
          this.exportToCSV(rows.join('\n'), fileName)
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
    },
    toLocalDateString(dateStr) {
      return toLocalDateString(dateStr)
    },
    getPercentageForCM(cm, order_type, sales_tag) {
      const default_value = this?.percentagesData?.default?.[order_type]?.[sales_tag]
      const custom = this?.percentagesData?.custom?.filter?.(item => item.campaign_manager === cm && item.order_type === order_type && item.sales_tag === sales_tag)?.[0]?.custom_commission_percentage
      const final = custom === undefined ? default_value : custom
      
      return {
        default: default_value,
        custom,
        final
      }
    },
    onFilter() {
      // console.log('filtering')
      this.$nextTick(() => {
        this.loadData()
      })
    },
    updateSale(row, payload) {
      this.$store.commit('showLoading');

      let promise

      if (row.order_type === 'audit_order') {
        promise = this.$store.dispatch('updateSiteContentAudit', {
          auditId: row.id,
          body: payload
        })
      } else if (['silo_order', 'quick_content_order'].includes(row.order_type)) {
        promise = this.$store.dispatch('updateSiloContentReportOrder', {
          orderId: row.id,
          body: payload
        })
      } else if (row.order_type === 'balance_top_up_order') {
        promise = this.$store.dispatch('updateBalanceTopUpOrder', {
          orderId: row.id,
          body: payload
        })
      }

      promise.then(response => {
        if (response.data && response.data.status == 'SUCCESS') {
          return this.loadData()
          .then(() => {
            this.isEditingPercentageForId = null;
          })
        }
      })
      .finally(() => {
        this.$store.commit('hideLoading');
      })
    },
    loadPercentages() {
      return this.$store.dispatch('getCampaignManagersPercentages')
        .then(({ data }) => {
          this.percentagesData = data
        })
    },
    updateCMPercentage(cm, payload) {
      this.$store.commit('showLoading');

      this.$store.dispatch('updateCampaignManagerPercentage', {
        key: cm,
        body: payload
      }).then(response => {
        if (response.data && response.data.status == 'SUCCESS') {
          return this.loadData()
          .then(() => {
            this.isEditingPercentageForCM = null;
          })
        }
      })
      .finally(() => {
        this.$store.commit('hideLoading');
      })
    },
    loadData() {
      this.isLoading = true
      this.$store.commit('showLoading')

      let startDate = this.dates[0]?moment(this.dates[0]).startOf('day').toDate():null;
      let endDate = this.dates[1]?moment(this.dates[1]).endOf('day').toDate():null;

      let params = [
        `filterText=${this.searchText}`,
        `campaignManager=${this.campaignManager}`,
        `commissionOwner=${this.commissionOwner}`,
        `salesTag=${this.salesTag}`,
        `orderType=${this.orderType}`,
      ]

      if (startDate) {
        params.push(`startDate=${startDate.getTime()}`)
      }
      if (endDate) {
        params.push(`endDate=${endDate.getTime()}`)
      }

      params = params.join('&')
      return this.$store.dispatch('getOrdersCommissionsForAdmin', { params })
        .then(({ data }) => {
          const { total, records, aggregations } = data
          this.totalRows = total
          this.records = records
          this.aggregations = aggregations
        })
        .then(() => this.loadPercentages())
        .finally(() => {
          this.isLoading = false
          this.$store.commit('hideLoading')
        })
    },
    changeSearchText() {
      console.log(this.searchText);
    }
  },
  beforeMount() {
    this.loadData()
  }
}
</script>

<style lang="scss">
.table-mobile-sort {
  display: none;
}

.sales-tag-dropdown {
  .dropdown-item {
    font-weight: normal;
    text-align: left;
  }

  &.is-disabled {
    opacity: initial;
    cursor: default;
  }
}

.sales-tag-btn {
  text-transform: initial;
  letter-spacing: initial;
}

.export-dropdown {
  width: 100%;
  .dropdown-trigger {
    width: 100%;
  }
}
</style>
